'use client';

import { useDebounce, useIntersectionObserver } from '@uidotdev/usehooks';
import { useEffect, useMemo, useState } from 'react';
import { QueryFunction, QueryKey, useInfiniteQuery, useQuery } from 'react-query';
import { IoSearch } from 'react-icons/io5';
import { ApiErrorResponse, ApiResponse, PaginatedApiResponse } from '../common/config/axios';
import Input from './forms/Input';
import ListItem from './ListItem';

export type ListProps<T = any> = {
  // Props
  containerClassName?: string;
  listClassName?: string;
  filterClassName?: string;
  emptyListText?: string;
  footer?: React.ReactNode;
  header?: React.ReactNode;
  // Data
  data?: T[];
  // Query
  queryKey?: QueryKey;
  queryFn?: QueryFunction<ApiResponse<T[]> | PaginatedApiResponse<T>, QueryKey>;
  // Events
  onSearch?: (searchText: string) => void;
  onData?: (data: T[]) => void;
  renderItem?: (item: T, index: number) => React.ReactNode;
};

function ListLocal<T>(props: ListProps<T>) {
  return (
    <div>
      {props.data?.map((item, index) => {
        return <div key={index}>{props.renderItem?.(item, index)}</div>;
      })}
    </div>
  );
}

export function ListApi<T>({
  containerClassName,
  emptyListText,
  footer,
  header,
  onData,
  onSearch,
  queryFn,
  queryKey,
  renderItem,
}: ListProps<T>) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const { data, error, isError, isFetching, refetch } = useQuery<ApiResponse<T[]>, ApiErrorResponse>({
    queryKey: queryKey,
    queryFn: queryFn as QueryFunction<ApiResponse<T[]>, QueryKey>,
    retry: false,
  });

  useEffect(() => {
    if (data?.data && onData) {
      onData(data.data.data);
    }
  }, [data, onData]);

  useEffect(() => {
    if (onSearch) {
      onSearch(debouncedSearch);
    }
  }, [debouncedSearch, onSearch]);

  return (
    <div className={containerClassName}>
      {header}
      {onSearch && (
        <div className='bg-background-50 p-4'>
          <Input placeholder='Search' onChange={(e: any) => setSearch(e.target.value)} />
        </div>
      )}

      {isError && (
        <div className='flex items-center justify-center p-4 text-red-500'>
          {error?.response?.data?.message || 'generic_error'}
        </div>
      )}

      {!isFetching && data?.data.data.length === 0 && (
        <div className='flex items-center justify-center p-4 text-foreground-500'>{emptyListText || 'empty_list'}</div>
      )}

      {data?.data.data?.map((item, index) => {
        return <div key={index}>{renderItem?.(item, index)}</div>;
      })}

      {footer}
    </div>
  );
}

export function ListApiInfinite<T>({
  listClassName,
  filterClassName,
  containerClassName,
  emptyListText,
  footer,
  header,
  onData,
  onSearch,
  queryFn,
  queryKey,
  renderItem,
}: ListProps<T>) {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);

  const [intersectionRef, intersectionEntry] = useIntersectionObserver({
    threshold: 0,
    root: null,
    rootMargin: '0px',
  });

  const { data, error, isError, isFetching, isFetchingNextPage, isLoading, fetchNextPage, refetch, hasNextPage } =
    useInfiniteQuery<PaginatedApiResponse<T>, ApiErrorResponse>({
      queryKey: queryKey,
      queryFn: queryFn as QueryFunction<PaginatedApiResponse<T>, QueryKey>,
      keepPreviousData: true,
      getNextPageParam: (lastPage: any) => {
        if (lastPage?.pagination?.current_page < lastPage?.pagination?.total_pages) {
          return lastPage?.pagination.current_page + 1;
        }
      },
      retry: false,
    });

  const listData: T[] = useMemo(() => {
    console.log(data);
    return data?.pages?.map((page) => page?.data).flat() || [];
  }, [data]) as T[];

  useEffect(() => {
    if (listData && onData) {
      onData(listData);
    }
  }, [listData, onData]);

  useEffect(() => {
    if (intersectionEntry?.isIntersecting) {
      if (hasNextPage) {
        fetchNextPage();
      }
    }
  }, [fetchNextPage, hasNextPage, intersectionEntry]);

  useEffect(() => {
    if (onSearch) {
      onSearch(debouncedSearch);
      console.log('DATA');
    }
  }, [debouncedSearch, onSearch]);

  return (
    <div className={`h-full overflow-x-auto ${containerClassName}`}>
      {header}

      {onSearch && (
        <div className={`w-full mb-4 mt-2 ${filterClassName}`}>
          <Input className='w-full' placeholder='Search' onChange={(e: any) => setSearch(e.target.value)} />
        </div>
      )}
      {isError && (
        <div className='flex items-center justify-center p-4 text-red-500'>
          {error?.response?.data?.message || 'generic_error'}
        </div>
      )}
      {!isFetching && listData.length === 0 && (
        <div className='flex items-center justify-center p-4 text-foreground-500'>
          {emptyListText || 'List is empty. Click on + to add items.'}
        </div>
      )}
      <main className={`bg-card rounded-lg overflow-hidden ${listClassName}`}>
        {listData?.map((item, index) => {
          return <ListItem key={index}>{renderItem?.(item, index)}</ListItem>;
        })}
      </main>

      {hasNextPage ? <div ref={intersectionRef} className='h-10 w-full bg-red-transparent' /> : null}
      {footer}
    </div>
  );
}
